import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { T, useGT } from 'gt-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CaretDownIcon, XIcon } from '@phosphor-icons/react';

/**
 * A batch number is a plain integer, so the >=/<=/between operators a generic
 * numeric filter offers don't map cleanly onto how operators think about it.
 * Instead this filter accepts a free-text expression and matches on equality:
 *   - "12345"            → that exact batch
 *   - "12345,12346,1300" → any of those batches (comma-separated list)
 *   - "12341-12349"      → every batch in the inclusive range
 *   - combinations: "12345, 12341-12349"
 *
 * Each comma-separated part is normalized to either "<n>" (exact) or
 * "<lo>-<hi>" (range). Parts are OR'd together by the backend.
 */
const ENTRY_RE = /^\d+(-\d+)?$/;

/** Parse a raw comma/range expression into normalized backend entries. */
export function parseBatchExpression(raw: string): string[] {
  return raw
    .split(',')
    .map((part) => part.trim())
    .filter((part) => ENTRY_RE.test(part));
}

interface BatchNumberFilterProps {
  label: string;
  value: string[];
  onChange: (values: string[] | undefined) => void;
}

export function BatchNumberFilter({
  label,
  value,
  onChange,
}: BatchNumberFilterProps) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const isActive = value.length > 0;
  const summary = useMemo(
    () => (value.length > 0 ? value.join(', ') : null),
    [value],
  );

  // Local input state for debounced updates.
  const [text, setText] = useState(value.join(', '));
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Cancel any pending debounce on unmount so a queued timeout can't fire
  // onChange with stale input after the component is gone.
  useEffect(() => {
    return () => {
      clearTimeout(timer.current);
    };
  }, []);

  // Sync local input when URL state changes externally (e.g. clear-all).
  const lastValueRef = useRef(value);
  if (value !== lastValueRef.current) {
    lastValueRef.current = value;
    setText(value.join(', '));
  }

  // Longer debounce than a typical search box: S&D operators are on slow
  // (~0.25 Mbps) connections, so we wait until they've clearly stopped typing
  // the batch number before firing a request, rather than after every digit.
  const DEBOUNCE_MS = 1000;

  const handleTextChange = useCallback(
    (raw: string) => {
      setText(raw);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => {
        const entries = parseBatchExpression(raw);
        onChange(entries.length > 0 ? entries : undefined);
      }, DEBOUNCE_MS);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    clearTimeout(timer.current);
    onChange(undefined);
    setText('');
    setOpen(false);
  }, [onChange]);

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={`h-7 justify-between font-normal max-w-[200px] ${isActive ? 'border-foreground' : 'text-muted-foreground'}`}
          >
            <span className="truncate">
              {isActive ? `${label}: ${summary}` : label}
            </span>
            <CaretDownIcon className="h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-3" align="start">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">{label}</p>
              {isActive && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={handleClear}
                >
                  <XIcon className="h-3 w-3 mr-1" />
                  <T context="button to clear the batch number filter">Clear</T>
                </Button>
              )}
            </div>

            <Input
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder={gt('e.g. 12345, 12341-12349', {
                context: 'placeholder for batch number filter input',
              })}
              className="h-8 text-sm"
            />

            <p className="text-xs text-muted-foreground">
              <T context="helper text explaining batch number filter syntax">
                Enter one batch (12345), a comma-separated list (12345,12346),
                or a range (12341-12349).
              </T>
            </p>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
